import React, { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Handshake, Loader2, AlertTriangle } from 'lucide-react';
import { CategorySpendBars, chartPalette, chartOtherColor } from '@techstack/ui';
import { useTimeFilterContext } from '../../contexts/TimeFilterContext';
import { useListsContext } from '../../contexts/ListsContext';
import { loggedGet } from './loggedFetch';
import logger from '../../lib/logger';

/**
 * PartnerCategorySpend — stacked-bar widget showing, for the selected vendor
 * (default Palo Alto Networks), WHICH RESELLERS/PARTNERS are selling that
 * vendor's products, broken down BY SECURITY CATEGORY.
 *
 * X = security category, Y = $, each bar stacked by partner. Reuses the shared
 * CategorySpendBars from @techstack/ui (segments = partners). Filters inherit
 * from the global time filter (dateRange) + account scope (departmentIds),
 * exactly like SecurityCoverageMatrix — no bespoke filter UI here.
 */

const QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  cacheTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
};

/* The partner ramp is imported, not restated. Slot n IS the partner's
   identity — same colour here, on the Dash, and in GAS — which only holds
   while there is ONE array. There were five. See @techstack/ui tokens.ts. */
const PARTNER_PALETTE = chartPalette;
const PARTNER_OTHER_COLOR = chartOtherColor;

function PartnerCategorySpend({ vendor, departmentIds, onDrilldown }) {
  const { dateRange } = useTimeFilterContext();
  const { selectedList } = useListsContext();

  const deptKey = departmentIds?.length ? departmentIds.join(',') : null;

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (deptKey) params.set('department_ids', deptKey);
    if (dateRange?.startDate) params.set('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.set('endDate', dateRange.endDate);
    return params.toString();
  }, [deptKey, dateRange]);

  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/partners-by-category${queryParams ? `?${queryParams}` : ''}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'partners-by-category', vendor, deptKey, dateRange?.startDate, dateRange?.endDate],
    queryFn: loggedGet('PartnerCategorySpend:fetch', path),
    ...QUERY_OPTIONS,
    enabled: !!vendor,
  });

  // Map endpoint shape → CategorySpendBars props (segments = partners).
  const chart = useMemo(() => {
    if (!data?.categories?.length) return null;
    const topVendors = (data.partners || []).map(p => p.partner);
    const colorMap = {};
    let ci = 0;
    topVendors.forEach((p) => {
      if (p === 'Other' || p === 'Unknown/Unattributed') {
        colorMap[p] = PARTNER_OTHER_COLOR;
      } else {
        colorMap[p] = PARTNER_PALETTE[ci % PARTNER_PALETTE.length];
        ci += 1;
      }
    });
    const chartData = data.categories.map((c) => ({
      categoryId: c.category,
      category: c.category,
      totalSpend: c.total_spend,
      totalSpendFormatted: c.total_spend_formatted,
      vendors: Object.fromEntries((c.partners || []).map((p) => [p.partner, p.spend])),
    }));
    return { chartData, topVendors, colorMap };
  }, [data]);

  const handleSegment = useCallback((info) => {
    if (!onDrilldown) return;
    const partner = info.vendor; // segment key = partner name
    const realPartner = partner && partner !== 'Other' && partner !== 'Unknown/Unattributed';
    logger.vendorAnalysis.info('PartnerCategorySpend:segment-click', {
      partner, category: info.category, spend: info.spend,
    });
    onDrilldown({
      supplier: realPartner ? partner : null,
      securityCategory: info.category,
      // Single-agency scope drills into that department; multi-scope shows all.
      departmentId: departmentIds?.length === 1 ? departmentIds[0] : undefined,
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
    });
  }, [onDrilldown, departmentIds, dateRange]);

  return (
    <motion.div
      className="partner-category-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <div className="security-coverage-header">
        <div className="security-coverage-title">
          <Handshake size={20} />
          <div>
            <h2>Partner Spend by Security Category</h2>
            <p>
              Which resellers are selling {vendor} products, by security category.
              {data?.summary?.total_spend_formatted && (
                <span> {' '}Total {data.summary.total_spend_formatted}.</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {departmentIds?.length > 0 && selectedList && (
        <div className="security-coverage-scope">
          Scoped to <strong>{selectedList.name}</strong> ({departmentIds.length} agencies)
        </div>
      )}

      {isLoading && (
        <div className="security-coverage-loading">
          <Loader2 size={16} className="security-coverage-spin" />
          Loading partner spend…
        </div>
      )}

      {error && (
        <div className="security-coverage-error">
          <AlertTriangle size={16} />
          Failed to load partner spend: {error.message}
        </div>
      )}

      {!isLoading && !error && chart && (
        <CategorySpendBars
          data={chart.chartData}
          topVendors={chart.topVendors}
          colorMap={chart.colorMap}
          height={480}
          maxBarSize={72}
          onSegmentClick={handleSegment}
          subtitle="Click a segment to drill into that partner's transactions."
        />
      )}

      {!isLoading && !error && !chart && (
        <div className="security-coverage-empty">
          <Handshake size={24} />
          <p>No {vendor} partner spend for the selected account and time range.</p>
        </div>
      )}
    </motion.div>
  );
}

export default PartnerCategorySpend;
